import 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { t } from 'ttag';
import cookie from 'js-cookie';
import dayjs from 'dayjs';

import SwapLogoIcon from '@svg/swap-logo.svg';
import FreeDelivery from '@svg/delivery.svg';
import UaFlag from '@svg/ua-flag.svg';
import PreloaderIcon from '@svg/preloader.svg';
import FreeDeliverySmartIcon from '@svg/free-delivery.svg';
import { useAppContext } from '@contexts/AppContext';
import { getPathWithoutLang } from '@utils/i18n';
import { getIsCanary, getProductModerationAdminPage, useIsClient } from '@utils/common';
import { Badge } from '@components/Badge';
import { Checkbox } from '@ui/Checkbox';
import { Stars } from '@ui/Stars';
import { Image } from '@ui/Image';
import { useRegion } from '@hook/useRegion';
import { useUserSubscriptionInfo } from '@hook/useUserSubscriptionInfo';
import {
  DeliveryService,
  ProductMultipleActionStatus,
  ProductSaleStatus,
  SellingConditionEnum,
  TagsEnum,
} from '@models';
import {
  ecommerceTrigger,
} from '@utils/analytics';
import { ADULT_USER_COOKIE } from '@constants';
import { ProductFavoritesToggleContainer } from '@containers/ProductFavoritesToggle';
import { ProductSaleLabel } from '@components/ProductSaleLabel';
import { Button } from '@ui/Button';

import adultPlaceholderImage from './images/adult-placeholder.svg';

import s from './ProductCard.sss';

// types
type Size = {
  name: string;
};

type Brand = {
  name: string;
};

type SaleLabel = {
  status: ProductSaleStatus;
  date: string;
  price: number
};

type Catalog = {
  slug: string
};

interface Seller {
  id: number,
}

type OnCheck = () => void;

// consts
const multipleActionVerbose = {
  [ProductMultipleActionStatus.Activating]: t`Активація...`,
  [ProductMultipleActionStatus.Deactivating]: t`Деактивація...`,
};

export type Product = {
  id: number;
  url: string;
  name: string;
  thumbnail: string;
  likes: number | undefined | null;
  price: number | null | undefined;
  size: Size[];
  brand?: Brand | null;
  catalog: Catalog | null | undefined
  saleLabel: SaleLabel;
  isLiked: boolean;
  isNew: boolean;
  isReserved?: boolean;
  isTop?: boolean;
  isVip?: boolean;
  isDisabled?: boolean;
  isOutOfStock?: boolean;
  isUkrainian: boolean;
  tags: TagsEnum[];
  oldPrice?: number | null;
  discountPercent?: number | null;
  freeDeliveryServices?: DeliveryService[];
  hasRecentActivity: boolean;
  rating?: number | null;
  ratingAmount?: number | null;
  seller?: Seller | null,
  isViewed?: boolean | null;
  freePushupAvailable?: boolean | null;
  createdAt?: string | null;
  sellingCondition?: SellingConditionEnum;
};

export type ProductCardProps = Product & {
  processingMultipleAction?: ProductMultipleActionStatus;
  promotion?: FeedProductPromotion;
  footerAction?: (product: ProductCardProps) => React.ReactElement | null;
  checked?: boolean;
  onCheck?: OnCheck;
  className?: string;
  onProductClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  productOpenBlank?: boolean;
  lazyImage?: boolean;
  type?: 'Product' | 'ProductDetailsProducts';
};

type ProductLinkPropsType = {
  target?: string;
};

// Show product image only when it fully loaded
if (typeof window !== 'undefined') {
  document.addEventListener('lazyloaded', (e) => {
    const { target } = e;
    if (target) {
      (target as HTMLElement).classList.add(s.imageLoaded);
    }
  });
}

const onCheckWithPreventDefault = (onCheck: OnCheck) => (
  (e: React.SyntheticEvent) => {
    e.preventDefault();
    onCheck();
  }
);

export const ProductCard: React.FC<ProductCardProps> = (props) => {
  const history = useHistory();
  const isClient = useIsClient();
  const region = useRegion();
  const { isOutlet } = useAppContext();
  const {
    viewer,
    smartInfo,
    loading: smartInfoLoading,
  } = useUserSubscriptionInfo();
  const {
    id,
    url,
    name,
    thumbnail,
    size,
    price,
    likes,
    brand,
    catalog,
    footerAction,
    checked,
    onCheck,
    onProductClick,
    isLiked,
    isDisabled,
    processingMultipleAction,
    className,
    oldPrice,
    discountPercent,
    saleLabel,
    isTop = false,
    isVip = false,
    isReserved = false,
    freeDeliveryServices,
    isUkrainian,
    tags,
    rating,
    ratingAmount,
    productOpenBlank = false,
    type = 'Product',
    seller,
    isViewed,
    createdAt,
    sellingCondition,
    lazyImage = true,
  } = props;
  const isOwner = viewer?.id === seller?.id;
  const isAdult = tags.includes(TagsEnum.AdultOnly);
  const isUserConfirmedAge = isClient && !!cookie.get(ADULT_USER_COOKIE);
  const promoFreeShipping = tags.includes(TagsEnum.FreeShipping);
  const isFreeDeliveryBadgeVisible = (
    (freeDeliveryServices?.length || promoFreeShipping)
    && (!viewer?.subscriptionInfo || viewer.id === seller?.id)
  );
  const isSmartBadgeVisible = (
    !isOwner
    && !smartInfoLoading
    && !!viewer?.subscriptionInfo
    && Number(price) >= smartInfo?.minPurchaseCondition
  );
  const isInSwap = (
    sellingCondition === SellingConditionEnum.Exchange
  );

  const isProcessingMultipleAction = (
    processingMultipleAction
    && processingMultipleAction !== ProductMultipleActionStatus.Default
  );

  const productLinkProps: ProductLinkPropsType = {};

  if (productOpenBlank) {
    productLinkProps.target = '_blank';
  }

  const handleProductCardClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    ecommerceTrigger({
      event: 'select_item',
      products: [{
        id,
        name,
        price,
        brand,
        catalog,
      }],
    });

    if (isOutlet) {
      e.preventDefault();

      history.push(getPathWithoutLang(url));
    }

    if (onProductClick) {
      onProductClick(e);
    }
  };

  return (
    (
      <div
        className={classNames(
          s.root,
          { [s.disabled]: isDisabled },
          { [s.processingMultipleAction]: isProcessingMultipleAction },
          { [s.highlighted]: isViewed === false },
          className,
        )}
      >
        <a className={s.link} href={url} onClick={handleProductCardClick} {...productLinkProps}>
          {
          checked !== undefined && onCheck
          && (
          <div
            className={s.checkboxOuter}
            onClick={onCheckWithPreventDefault(onCheck)}
          >
            <Checkbox
              className={s.checkbox}
              checked={checked}
              onCheck={onCheck}
            />
          </div>
          )
        }
          <div className={s.imagePlaceholder}>
            <div className={s.badges}>
              {
              isUkrainian
                ? (
                  <Badge isImage={true} className={s.badge}>
                    <UaFlag className={s.uaBadgeIcon} />
                  </Badge>
                )
                : null
            }
              {
              isTop
                ? <Badge text="top" color="red" className={s.badge} />
                : null
            }
              {
              isVip
                ? <Badge text="vip" color="red" className={s.badge} />
                : null
            }
              {
              isReserved
                ? <Badge text="резерв" color="orange" className={s.badge} />
                : null
            }
              {isFreeDeliveryBadgeVisible ? (
                <Badge isImage={true} className={s.badge}>
                  <FreeDelivery className={s.badgeIcon} />
                </Badge>
              ) : null}
              {isSmartBadgeVisible ? (
                <Badge isImage={true} className={s.badge}>
                  <FreeDeliverySmartIcon
                    className={s.badgeIcon}
                    width="16"
                    height="16"
                  />
                </Badge>
              ) : null}
              {isInSwap && (
                <Badge isImage={true} className={s.badge}>
                  <SwapLogoIcon
                    className={s.badgeIcon}
                    width="16"
                    height="16"
                  />
                </Badge>
              )}
            </div>
            <div
              className={classNames(s.ratioContainer, {
                [s.ratioContainerTypeAdult]: isAdult,
              })}
            >
              {isAdult && !isUserConfirmedAge && !isOwner ? (
                <Image
                  className={s.image}
                  src={adultPlaceholderImage}
                  lazy={true}
                  width="96"
                  height="72"
                  alt={name}
                />
              ) : (
                <Image
                  className={classNames(s.image, { [s.imageLoaded]: !lazyImage })}
                  src={thumbnail}
                  lazy={lazyImage}
                  alt={name}
                />
              )}
              <PreloaderIcon className={s.imagePreloader} />
            </div>
            {brand ? <div className={s.brand}>{brand.name}</div> : null}
            <div className={classNames(s.multipleAction)}>
              {
              isProcessingMultipleAction && processingMultipleAction
                ? multipleActionVerbose[processingMultipleAction]
                : null
            }
            </div>
          </div>
        </a>
        <div className={s.cardFooterInfo}>
          <footer className={s.info}>
            <div className={s.priceBlock}>
              <div
                className={
                classNames(
                  s.price,
                  { [s.sale]: saleLabel.status === ProductSaleStatus.SaleStarted },
                )
              }
              >
                {isInSwap ? (
                  <p className={s.swap}>
                    SWAP
                  </p>
                ) : (
                  <>
                    <p>
                      {price ? `${price} ${region.currencyName}` : t`Безкоштовно`}
                    </p>

                    {oldPrice && (
                      <div className={s.discountArea}>
                        <span className={s.oldPrice}>
                          {oldPrice}
                          {' '}
                          {region.currencyName}
                        </span>
                        {
                          discountPercent
                          && saleLabel.status === ProductSaleStatus.SaleNone && (
                            <div className={s.discount}>
                              -
                              {discountPercent}
                              %
                            </div>
                          )
                        }
                      </div>
                    )}

                    <ProductSaleLabel
                      className={s.saleLabel}
                      status={saleLabel.status}
                      date={saleLabel.date}
                      price={saleLabel.price}
                    />
                  </>
                )}
              </div>
              {!isOutlet && (
                <ProductFavoritesToggleContainer
                  product={{
                    id,
                    name,
                    price: price || 0,
                    likes: likes || 0,
                    isLiked,
                    brand,
                    catalog,
                  }}
                  type={type}
                />
              )}
            </div>
            <div>
              <a href={url} className={s.aboutTitle} onClick={handleProductCardClick} {...productLinkProps}>
                {name}
              </a>
              <p className={s.sizes}>
                {
                size[0]
                  ? size[0].name
                  : ''
              }
                { size.length > 1 && (
                <span className={s.moreSize}>
                  {t`і ще`}
                  {' '}
                  {size.length - 1}
                </span>
                )}
              </p>

              {rating && ratingAmount && (
              <div className={s.rating}>
                <Stars
                  value={rating}
                  readOnly={true}
                />
                &nbsp;
                {`(${ratingAmount})`}
              </div>
              )}
            </div>
          </footer>

          {footerAction ? (
            <div className={s.footerAction}>
              {footerAction(props)}
            </div>
          ) : (
            <>
              {isClient && getIsCanary() && (
              <div className={s.footerAction}>
                <Button
                  href={getProductModerationAdminPage(id)}
                  target="_blank"
                >
                  на модерацію
                </Button>
                {!!createdAt && (
                <>
                  <br />
                  <b>
                    {dayjs(createdAt).format('DD-MM-YY HH:mm')}
                  </b>
                </>
                )}
              </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  );
};
